import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import Table, {
  getRowData,
  columns as defaultColumns,
  getServiceTypeAttributes,
} from "components/Table";
import { speciesMap, useHandleRequestStates } from "utils";
import { getTableColumns, getChangedAttributes } from "../../utils";

const SupportServicesTable = forwardRef(
  ({ id, attributes, species, isEditing, submit }, ref) => {
    const { handleSuccess } = useHandleRequestStates();

    const columns = getTableColumns({ defaultColumns, isEditing, species });

    const setRow = ({ rowLabel, keySubString, tooltip }) =>
      getRowData({
        data: attributes,
        accessKeys: ["adult", "youth", "ageUnknown", "total"],
        rowLabel,
        keySubString,
        tooltip,
      });

    const data = [
      setRow({
        rowLabel: "Pet Food",
        keySubString: "PetFoodCount",
        tooltip:
          "Animals receiving pet food through pet pantries or similar services.",
      }),
      setRow({
        rowLabel: "Pet Supplies",
        keySubString: "PetSuppliesCount",
        tooltip:
          "Animals receiving leashes and collars, cat carriers, or other supplies.",
      }),
      setRow({
        rowLabel: "Grooming",
        keySubString: "GroomingCount",
        tooltip: "Animals receiving grooming services.",
      }),
      setRow({
        rowLabel: "Temporary Housing for Owned Animals",
        keySubString: "TemporaryHousingOwnedAnimalsCount",
        tooltip:
          "Animals receiving temporary short or long term housing WITHOUT their owner.",
      }),
      setRow({
        rowLabel: "Temporary Housing for People and Animals",
        keySubString: "TemporaryHousingPeopleAndAnimalsCount",
        tooltip:
          "Animals receiving temporary short or long term housing WITH their owner.",
      }),
      setRow({
        rowLabel: "Other Pet Retention Services",
        keySubString: "OtherPetRetentionServicesCount",
        tooltip:
          "Animals receiving other support not captured above (i.e. fence repairs/builds, dog houses, pet deposits).",
      }),
      setRow({
        rowLabel: "Rehoming Support",
        keySubString: "RehomingSupport",
        tooltip:
          "Animals receiving rehoming assistance outside of owner relinquishment or other shelter/rescue intake.",
      }),
    ];

    const handleSubmit = async (values) => {
      handleSuccess({
        message: `Updated the ${speciesMap[
          attributes.species
        ].label.toLocaleLowerCase()} support services`,
      });

      await submit(id, getChangedAttributes("support", attributes, values));
    };

    return (
      <div className="mt-12">
        <div className="flex items-center justify-between space-x-4 mb-4">
          <h3 className="text-2xl flex items-center">
            <img src={speciesMap[species].icon} alt="" className="mr-4" />
            {speciesMap[species].label}
          </h3>
        </div>

        <Formik
          enableReinitialize
          initialValues={getServiceTypeAttributes(data, attributes)}
          onSubmit={(values) => handleSubmit(values)}
          innerRef={ref}
        >
          {() => (
            <Form>
              {/* Form Table */}
              <Table
                data={data}
                columns={columns}
                columnAlignments={[null, null, null, null, "text-right"]}
              />
            </Form>
          )}
        </Formik>
      </div>
    );
  }
);

SupportServicesTable.propTypes = {
  attributes: PropTypes.object.isRequired,
  species: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
};

export default SupportServicesTable;
